import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { NodeEditIcon, StrokeCenterIcon, SubnodeAddIcon } from '@hugeicons-pro/core-bulk-rounded'
import { Box, Button, Checkbox, IconButton, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { selectOrderItems } from '@/app/services/orderItems'
import { useGetSecondaryProcessesQuery } from '@/app/services/secondaryProcesses'
import { selectSelectedItems } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId, selectWebStoreFoldingEnabled } from '@/app/slices/organisationSlice'
import QuantitySecProcess from '@/features/secondary-processes/QuantitySecProcess'
import SecondaryProcessRow from '@/features/secondary-processes/SecondaryProcessRow'

import FoldingBulkEdit from './FoldingBulkEdit'
import SecondaryProcessesBulkEditModal from './SecondaryProcessesBulkEditModal'

const classes = {
    bold: {
        fontWeight: 'bold',
    },
    formControl_small: {
        width: '80px',
    },
}

const SecondaryProcessesBulkEdit = ({
    itemMaxLength,
    itemNumberOfFolds,
    itemSecondaryProcesses,
    itemShowFolding,
    itemSource,
    itemUnit,
    setItemMaxLength,
    setItemNumberOfFolds,
    setItemSecondaryProcesses,
    setItemShowFolding,
    setItemSource,
    setUserMadeFoldingChanges,
    setUserMadeSecProcessesChanges,
}) => {
    const { t } = useTranslation()
    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const foldingEnabled = useSelector(selectWebStoreFoldingEnabled)
    const orderItems = useSelector((state) => selectOrderItems(state, { organisationId, orderId }))
    const selectedItemsArray = useSelector(selectSelectedItems)

    const { data: organisationSecondaryProcesses } = useGetSecondaryProcessesQuery({ organisationId })

    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

    const orderItemsFromSelection = useMemo(
        () => orderItems.filter((item) => selectedItemsArray.includes(item.id)),
        [orderItems, selectedItemsArray]
    )

    const partSecProcs = useMemo(
        () => orderItemsFromSelection.map((part) => part.secondaryProcesses).reduce((a, b) => [...a, ...b], []),
        [orderItemsFromSelection]
    )

    const partsAreRotary = useMemo(() => {
        return orderItemsFromSelection.every((item) => 'profile' in item)
    }, [orderItemsFromSelection])

    const getProcessDetails = (processId) =>
        organisationSecondaryProcesses?.find((osp) => osp.secondaryProcessId === processId)

    const processesList = useMemo(() => {
        return itemSecondaryProcesses?.map((process) => ({
            ...process,
            index: getProcessDetails(process.secondaryProcessId)?.index,
        }))
    }, [itemSecondaryProcesses])

    const partSecProcsIds = useMemo(
        () => [
            ...new Set(
                orderItemsFromSelection
                    ?.map((part) => part.secondaryProcesses)
                    ?.reduce((a, b) => [...a, ...b], [])
                    ?.map((proc) => proc.secondaryProcessId)
            ),
        ],
        [orderItemsFromSelection]
    )

    const newSecondaryProcessList = useMemo(
        () => organisationSecondaryProcesses.filter((sp) => partSecProcsIds.includes(sp.secondaryProcessId)),
        [organisationSecondaryProcesses, partSecProcsIds]
    )

    const secondaryProcs = useMemo(
        () =>
            newSecondaryProcessList?.map((secProc) => {
                const { measureType, name, secondaryProcessId } = secProc
                let input = ''
                let enabled = false

                if (partSecProcs && partSecProcs.length > 0) {
                    // Evaluate how many orderItems have this process enabled
                    const partsWithThisProcess = partSecProcs.filter(
                        (proc) => proc.secondaryProcessId === secProc.secondaryProcessId
                    )

                    const inAllParts = partsWithThisProcess.length === orderItemsFromSelection.length
                    const notInParts = partsWithThisProcess.length === 0
                    const inSomeParts = !inAllParts && partsWithThisProcess.length > 0

                    if (inAllParts) {
                        enabled = true
                    } else if (inSomeParts) {
                        enabled = null
                    } else if (notInParts) {
                        enabled = false
                    }

                    if (measureType === 'Quantity') {
                        const allInputsEqual = partsWithThisProcess.every(
                            (proc) => proc.input === partsWithThisProcess[0].input
                        )
                        if (inAllParts && allInputsEqual) {
                            input = partsWithThisProcess[0]?.input
                        }
                    }
                }
                return {
                    secondaryProcessId,
                    input,
                    isEnabled: enabled,
                    name,
                    measureType,
                }
            }),
        [newSecondaryProcessList, partSecProcs, orderItemsFromSelection]
    )

    const handleEditButtonClick = (event) => {
        event.stopPropagation()
        setIsEditDialogOpen(true)
    }

    const onCloseEditDialog = (processes) => {
        setIsEditDialogOpen(false)
        if (processes) {
            setUserMadeSecProcessesChanges(true)
            setItemSecondaryProcesses(processes)
        }
    }

    const onSaveCloseEditDialog = async (processes) => {
        setIsEditDialogOpen(false)

        //handle empty processes
        if (!processes?.length) {
            setItemSecondaryProcesses([])
            setItemMaxLength(0)
            setItemNumberOfFolds(0)
            setItemSource('Manual')
            setUserMadeSecProcessesChanges(true)
            setUserMadeFoldingChanges(true)
            setItemShowFolding(false)
        }

        if (processes?.length) {
            const hasFolding = processes.find((process) => process.secondaryProcessId === 'Folding')
            const otherProcesses = processes?.filter((process) => process.secondaryProcessId !== 'Folding')

            if (hasFolding) {
                setItemShowFolding('all')
                setItemMaxLength(itemMaxLength)
                setItemNumberOfFolds(itemNumberOfFolds)
                setItemSource(itemSource)
                setUserMadeFoldingChanges(true)
            } else {
                setItemShowFolding(false)
                setItemMaxLength(0)
                setItemNumberOfFolds(0)
                setItemSource('Manual')
                setUserMadeFoldingChanges(true)
            }

            setItemSecondaryProcesses(otherProcesses)
            setUserMadeSecProcessesChanges(true)
        }
    }

    const onQuantityInputChange = (process) => {
        const processIds = itemSecondaryProcesses?.map((sp) => sp.secondaryProcessId)
        const processIndex = processIds.indexOf(process.secondaryProcessId)
        const newProcessList = [...itemSecondaryProcesses]
        newProcessList[processIndex] = process
        setItemSecondaryProcesses(newProcessList)
        setUserMadeSecProcessesChanges(true)
    }

    useEffect(() => {
        setItemShowFolding(
            orderItemsFromSelection?.every((orderItem) => orderItem?.numberOfFolds > 0)
                ? 'all'
                : orderItemsFromSelection?.some((orderItem) => orderItem?.numberOfFolds > 0)
                  ? 'some'
                  : false
        )
    }, [orderItemsFromSelection, setItemShowFolding])

    useEffect(() => {
        setItemSecondaryProcesses(secondaryProcs)
    }, [secondaryProcs, setItemSecondaryProcesses])

    return (
        <Box
            sx={{
                alignItems: 'flex-start',
                display: 'flex',
                flex: '1',
                flexDirection: 'column',
                width: '100%',
                gap: 2,
            }}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                    }}
                >
                    <HugeiconsIcon icon={StrokeCenterIcon} />
                    <Typography
                        data-testid="bulk-edit-secondary-processes-section-title"
                        sx={classes.bold}
                    >
                        {t('Secondary processes')}
                    </Typography>
                </Box>

                {itemSecondaryProcesses?.length || (foldingEnabled && itemShowFolding) ? (
                    <Button
                        aria-label="edit"
                        color="primary"
                        data-testid="secondary-processes-edit-button"
                        size="small"
                        startIcon={
                            <HugeiconsIcon
                                icon={NodeEditIcon}
                                size={21}
                            />
                        }
                        title={t('Edit secondary processes')}
                        onClick={(event) => handleEditButtonClick(event)}
                        onFocus={(event) => event.stopPropagation()}
                    >
                        {t('Edit')}
                    </Button>
                ) : null}
            </Box>

            {!itemSecondaryProcesses?.length && !(foldingEnabled && itemShowFolding) ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        height: '200px',
                    }}
                >
                    <Button
                        data-testid="secondary-processes-add-button"
                        startIcon={<HugeiconsIcon icon={SubnodeAddIcon} />}
                        variant="outlined"
                        onClick={(event) => handleEditButtonClick(event)}
                    >
                        {t('Add secondary processes')}
                    </Button>
                </Box>
            ) : null}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                {foldingEnabled && itemShowFolding && !partsAreRotary ? (
                    <FoldingBulkEdit
                        itemMaxLength={itemMaxLength}
                        itemNumberOfFolds={itemNumberOfFolds}
                        itemShowFolding={itemShowFolding}
                        itemSource={itemSource}
                        itemUnit={itemUnit}
                        setItemMaxLength={setItemMaxLength}
                        setItemNumberOfFolds={setItemNumberOfFolds}
                        setItemSource={setItemSource}
                        setUserMadeFoldingChanges={setUserMadeFoldingChanges}
                    />
                ) : null}
                {processesList?.map((process) => (
                    <SecondaryProcessRow
                        justifyContent="space-between"
                        key={process.secondaryProcessId}
                    >
                        <Box
                            alignItems="center"
                            display={'flex'}
                        >
                            <Checkbox
                                checked={process.isEnabled}
                                color="secondary"
                                data-testid="bulk-edit-secondary-process-option-checkbox"
                                indeterminate={process.isEnabled === null}
                                size="small"
                                sx={{ p: 0, mr: 1 }}
                                value={process.secondaryProcessId}
                            />
                            <Typography
                                data-testid="bulk-edit-secondary-process-label"
                                variant="body2"
                            >
                                {getProcessDetails(process.secondaryProcessId)?.name}
                            </Typography>
                        </Box>

                        {getProcessDetails(process.secondaryProcessId)?.measureType === 'Quantity' ? (
                            <QuantitySecProcess
                                process={process}
                                onInputChange={onQuantityInputChange}
                            />
                        ) : null}
                    </SecondaryProcessRow>
                ))}
            </Box>
            {isEditDialogOpen ? (
                <SecondaryProcessesBulkEditModal
                    open={isEditDialogOpen}
                    partsAreRotary={partsAreRotary}
                    secondaryProcesses={itemSecondaryProcesses}
                    showFolding={itemShowFolding}
                    onClose={onCloseEditDialog}
                    onSaveClose={onSaveCloseEditDialog}
                />
            ) : null}
        </Box>
    )
}

SecondaryProcessesBulkEdit.propTypes = {
    itemMaxLength: PropTypes.string,
    itemNumberOfFolds: PropTypes.string,
    itemSecondaryProcesses: PropTypes.array,
    itemShowFolding: PropTypes.string,
    itemSource: PropTypes.string,
    itemUnit: PropTypes.string,
    setItemMaxLength: PropTypes.func,
    setItemNumberOfFolds: PropTypes.func,
    setItemSecondaryProcesses: PropTypes.func,
    setItemShowFolding: PropTypes.func,
    setItemSource: PropTypes.func,
    setUserMadeFoldingChanges: PropTypes.func,
    setUserMadeSecProcessesChanges: PropTypes.func,
}

export default SecondaryProcessesBulkEdit
